import { useMemo } from 'react';
import { pick, prop } from 'ramda';
import { isObject } from 'underscore';
import { useAppSelector, useGameConfigurationSelector } from 'js/store/hooks';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentProps } from 'js/hooks/component/useComponentProps';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as Types from '@core/js/components/main/BonusHistory/types';
import * as helpers from '@core/js/components/main/BonusHistory/helpers';
import { IBonusTemplateDetails } from '@core/js/hooks/socket/types';
import { EMPTY_OBJECT } from '../../../constants';

export interface ICurrentBonusParsed extends Types.IBonusParsed {
    goldenChipsCount?: number | string;
    bonusTemplate?: IBonusTemplateDetails;
    gamingContext?: {
        selectedGames?: string[]
    };
}

export const useBonusesFormatter = <T extends ICurrentBonusParsed>(bonuses: T[] = [], isAlreadyFormatted = false) => {
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const games = useGameConfigurationSelector(prop('games')) || window?.Playtech?.Variables?.games || EMPTY_OBJECT;
    const { showcancelbutton } = <Types.IBonusHistoryConfig>useComponentProps();
    const { bonusesConfiguration, bonusManagementTCPreferences } = useAppSelector(pick(['bonusesConfiguration', 'bonusManagementTCPreferences']));
    const { bonusTcMapping, defaultTermsAndCondWcId } = bonusManagementTCPreferences;

    return useMemo(() => bonuses.map((bonus: ICurrentBonusParsed): Types.IBonusToDisplay => {
        const { amount, betLimit, acceptDate, amountOnBalance, completionType, endDate,
            freeSpinsCount, freeSpinsCountRemaining, goldenChips, initialWagering, pendingWinnings,
            playType, redeemedAmount, removalReason, status, templateCode, wagering,
            goldenChipsCount, playableGameTypes
        } = bonus;

        const isWageringAmountPresent = Boolean(wagering && Number(wagering.amount));
        const isInitialWageringAmountPresent = Boolean(initialWagering && Number(initialWagering.amount));
        const progressBarValue = isWageringAmountPresent && isInitialWageringAmountPresent
            ? String(100 - Math.round(Number(wagering?.amount) / Number(initialWagering?.amount || 1) * 100)) : '';
        const bonusConfig = bonusesConfiguration.find((element) => element.bonusCode === templateCode);
        const bonusTC = bonusTcMapping?.find((element) => element.bonusCode === templateCode);

        const { selectedGames = [] } = bonus?.gamingContext ? bonus.gamingContext || bonus : {};
        const gameCodes = (playableGameTypes || selectedGames).filter((code: string) => games[code]);

        const parsedGoldenChipsCount = goldenChipsCount ? goldenChipsCount.toString() : '';

        const currentAmount = isObject(amount) ? formatter.formatCurrency(amount?.amount, { currencyCode: amount?.currencyCode }) : amount || '';

        return {
            ...bonus,
            progressBarValue,
            popupTitle: translate('playtech.mobile.system.bonusDetail.tcPopupTitle'),
            detailedWebContentId: bonusConfig?.detailedViewWebContentId,
            popupWcId: bonusTC?.termsAndCondWcId || defaultTermsAndCondWcId || '',
            goldenChipsCount: goldenChips ? String(goldenChips.goldenChipsCount) : parsedGoldenChipsCount,
            freeSpinsCount: freeSpinsCount !== undefined ? String(freeSpinsCount) : '',
            goldenChipsCountRemaining: goldenChips ? String(goldenChips.goldenChipsCountRemaining) : '',
            freeSpinsCountRemaining: freeSpinsCountRemaining !== undefined ? String(freeSpinsCountRemaining) : '',
            endDate: endDate && formatter.getDateTimeShort(formatter.parseImsDate(endDate)) || '',
            acceptDate: acceptDate && formatter.getDateTimeShort(formatter.parseImsDate(acceptDate)) || '',
            amount: currentAmount,
            removalReason: removalReason && translate(`removalReason.${removalReason}`) || '',
            completionType: completionType && translate(`completionType.${completionType}`) || '',
            isCancelButton: typeof showcancelbutton === 'boolean'
                ? Boolean(showcancelbutton) && (status === 'active' || status === 'accepted' || status === 'waiting')
                 // @ts-expect-error: Unreachable code error
                : status === 2 || status === 3,
            betLimit: betLimit && formatter.formatCurrency(betLimit?.amount, { currencyCode: betLimit.currencyCode }) || '',
            amountOnBalance: formatter.formatCurrency(amountOnBalance?.amount, { currencyCode: amountOnBalance?.currencyCode }),
            redeemedAmount: redeemedAmount && formatter.formatCurrency(redeemedAmount.amount, { currencyCode: redeemedAmount.currencyCode }) || '',
            pendingWinnings: pendingWinnings && Number(pendingWinnings.amount) > 0 ? formatter.formatCurrency(pendingWinnings.amount, { currencyCode: pendingWinnings.currencyCode }) : '',
            initialWagering: initialWagering && Number(initialWagering.amount) ? String(formatter.formatCurrency(initialWagering.amount, { currencyCode: initialWagering.currencyCode })) : '',
            bonusAmount: bonus.amount ? helpers.getBonusAmount({ bonus, translate, formatCurrency: formatter.formatCurrency }) : '',
            wagering: wagering && Number(wagering.amount) ? formatter.formatCurrency(wagering.amount, { currencyCode: wagering.currencyCode }) : '',
            goldenChipValue: playType === 'goldenchips' && goldenChips?.goldenChipValue ? formatter.formatCurrency(goldenChips.goldenChipValue.amount, { currencyCode: goldenChips.goldenChipValue.currencyCode }) : '',
            ...(isAlreadyFormatted && bonus as unknown as Types.IBonusToDisplay),
            playableGameTypes: gameCodes
        };

    }), [
        bonusTcMapping,
        bonuses,
        bonusesConfiguration,
        defaultTermsAndCondWcId,
        formatter,
        games,
        isAlreadyFormatted,
        showcancelbutton,
        translate
    ]);
};
